import React from 'react';
import { useAuth, extractRoles } from './keycloak';

const Debug = () => {
  const { keycloak, token, roles } = useAuth();

  if (!keycloak?.tokenParsed) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6">
          <h1 className="text-2xl font-bold text-red-800 mb-4">❌ No hay token disponible</h1>
          <p className="text-red-700">El usuario no está autenticado o el token no está disponible.</p>
        </div>
      </div>
    );
  }

  const tokenParsed = keycloak.tokenParsed;

  // Extraer información detallada del token
  const tokenInfo = {
    // Información básica del usuario
    user: {
      username: tokenParsed.preferred_username,
      name: tokenParsed.name,
      email: tokenParsed.email,
      userId: tokenParsed.sub,
    },
    // Roles del realm
    realmRoles: tokenParsed.realm_access?.roles || [],
    // Todos los resource_access
    resourceAccess: tokenParsed.resource_access || {},
    // Groups si existen
    groups: tokenParsed.groups || [],
    // Claims personalizados
    customClaims: {},
    // Token completo (sin información sensible)
    fullToken: { ...tokenParsed }
  };

  // Extraer claims personalizados (cualquier propiedad que no sea estándar)
  const standardClaims = [
    'iss', 'sub', 'aud', 'exp', 'iat', 'auth_time', 'jti', 'typ',
    'preferred_username', 'name', 'email', 'email_verified',
    'realm_access', 'resource_access', 'scope', 'groups'
  ];
  
  Object.keys(tokenParsed).forEach(key => {
    if (!standardClaims.includes(key)) {
      tokenInfo.customClaims[key] = tokenParsed[key];
    }
  });

  // Función para mostrar JSON de forma legible
  const JsonDisplay = ({ data, title }) => (
    <div className="mb-6">
      <h3 className="text-lg font-semibold text-gray-800 mb-2">{title}</h3>
      <pre className="bg-gray-100 p-4 rounded-lg text-sm overflow-x-auto text-gray-700">
        {JSON.stringify(data, null, 2)}
      </pre>
    </div>
  );

  // Análisis de roles
  const roleAnalysis = () => {
    const analysis = {
      realmRolesCount: tokenInfo.realmRoles.length,
      clientRolesCount: Object.keys(tokenInfo.resourceAccess).length,
      hasAdminRole: false,
      adminRoleLocation: null,
      allRolesFound: []
    };

    // Buscar el rol admin en todas las ubicaciones
    if (tokenInfo.realmRoles.includes('admin')) {
      analysis.hasAdminRole = true;
      analysis.adminRoleLocation = 'realm_access.roles';
    }

    Object.keys(tokenInfo.resourceAccess).forEach(clientId => {
      const clientRoles = tokenInfo.resourceAccess[clientId].roles || [];
      analysis.allRolesFound.push(...clientRoles.map(role => `${clientId}.${role}`));
      if (clientRoles.includes('admin')) {
        analysis.hasAdminRole = true;
        analysis.adminRoleLocation = `resource_access.${clientId}.roles`;
      }
    });

    if (tokenInfo.groups.some(group => 
      (typeof group === 'string' && group === 'admin') || 
      (group.name && group.name === 'admin')
    )) {
      analysis.hasAdminRole = true;
      analysis.adminRoleLocation = 'groups';
    }

    analysis.allRolesFound.push(...tokenInfo.realmRoles.map(role => `realm.${role}`));

    return analysis;
  };

  const analysis = roleAnalysis();

  return (
    <div className="container mx-auto px-4 py-8 max-w-6xl">
      <div className="bg-white rounded-lg shadow-lg p-6">
        <h1 className="text-3xl font-bold text-gray-800 mb-6">🔍 Diagnóstico de Keycloak</h1>
        
        {/* Resumen del análisis */}
        <div className="bg-gray-50 rounded-lg p-4 mb-6">
          <h2 className="text-xl font-semibold text-gray-800 mb-3">📊 Resumen del Análisis</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <div className="bg-white p-3 rounded border">
              <div className="text-sm text-gray-600">Usuario</div>
              <div className="font-semibold">{tokenInfo.user.username}</div>
            </div>
            <div className="bg-white p-3 rounded border">
              <div className="text-sm text-gray-600">Roles del Realm</div>
              <div className="font-semibold">{analysis.realmRolesCount}</div>
            </div>
            <div className="bg-white p-3 rounded border">
              <div className="text-sm text-gray-600">Clientes con Roles</div>
              <div className="font-semibold">{analysis.clientRolesCount}</div>
            </div>
            <div className={`bg-white p-3 rounded border ${analysis.hasAdminRole ? 'border-green-300 bg-green-50' : 'border-red-300 bg-red-50'}`}>
              <div className="text-sm text-gray-600">Rol Admin</div>
              <div className="font-semibold">
                {analysis.hasAdminRole ? '✅ Encontrado' : '❌ No encontrado'}
              </div>
              {analysis.adminRoleLocation && (
                <div className="text-xs text-gray-500 mt-1">
                  En: {analysis.adminRoleLocation}
                </div>
              )}
            </div>
            <div className="bg-white p-3 rounded border">
              <div className="text-sm text-gray-600">Roles Extraídos</div>
              <div className="font-semibold">{roles.length}</div>
            </div>
            <div className="bg-white p-3 rounded border">
              <div className="text-sm text-gray-600">Groups</div>
              <div className="font-semibold">{tokenInfo.groups.length}</div>
            </div>
          </div>
        </div>

        {/* Roles actuales de la aplicación */}
        <div className="mb-6">
          <h2 className="text-xl font-semibold text-gray-800 mb-3">🎭 Roles Activos en la Aplicación</h2>
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            {roles.length > 0 ? (
              <div className="flex flex-wrap gap-2">
                {roles.map(role => (
                  <span 
                    key={role} 
                    className={`px-3 py-1 rounded-full text-sm font-medium ${
                      role === 'admin' ? 'bg-green-100 text-green-800' : 'bg-blue-100 text-blue-800'
                    }`}
                  >
                    {role}
                  </span>
                ))}
              </div>
            ) : (
              <p className="text-gray-600 italic">No se encontraron roles</p>
            )}
          </div>
        </div>

        {/* Todos los roles encontrados */}
        <div className="mb-6">
          <h2 className="text-xl font-semibold text-gray-800 mb-3">🔍 Todos los Roles en el Token</h2>
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            {analysis.allRolesFound.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                {analysis.allRolesFound.map((role, index) => (
                  <div key={index} className="text-sm font-mono bg-white px-2 py-1 rounded border">
                    {role}
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-600 italic">No se encontraron roles en el token</p>
            )}
          </div>
        </div>

        {/* Información del usuario */}
        <JsonDisplay 
          data={tokenInfo.user} 
          title="👤 Información del Usuario" 
        />

        {/* Roles del realm */}
        <JsonDisplay 
          data={tokenInfo.realmRoles} 
          title="🏰 Roles del Realm (realm_access.roles)" 
        />

        {/* Resource access */}
        <JsonDisplay 
          data={tokenInfo.resourceAccess} 
          title="🔑 Acceso a Recursos (resource_access)" 
        />

        {/* Groups */}
        <JsonDisplay 
          data={tokenInfo.groups} 
          title="👥 Grupos (groups)" 
        />

        {/* Claims personalizados */}
        {Object.keys(tokenInfo.customClaims).length > 0 && (
          <JsonDisplay 
            data={tokenInfo.customClaims} 
            title="🏷️ Claims Personalizados" 
          />
        )}

        {/* Token completo */}
        <div className="border-t pt-6">
          <details>
            <summary className="text-lg font-semibold text-gray-800 cursor-pointer hover:text-gray-600">
              🔒 Ver Token Completo (Expandir)
            </summary>
            <div className="mt-4">
              <JsonDisplay 
                data={tokenInfo.fullToken} 
                title="Token JWT Decodificado" 
              />
            </div>
          </details>
        </div>

        {/* Acciones de administración */}
        <div className="mt-6 flex flex-wrap gap-3">
          <button 
            onClick={() => window.location.reload()} 
            className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
          >
            🔄 Recargar
          </button>
          <button 
            onClick={() => keycloak?.logout()} 
            className="px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600"
          >
            🚪 Cerrar Sesión
          </button>
        </div>
      </div>
    </div>
  );
};

export default Debug;
